import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { usePanel } from 'hooks/usePanel';
import { attributionsPlateformesAPI } from 'services/swagger-api';
import { CollaborateurBriquesPlateformeDto } from 'api/attributions-plateformes';
import { InlineError } from 'back-office/shared/components/rolesUi';
import { BriquesCollaborateurForm } from './BriquesCollaborateurForm';

interface CabinetCollaborateursPanelProps {
  /** Absent = mode GOUVERNANCE (plateforme active de l'appelant, gardes serveur). */
  plateformeId?: string;
  personneMoraleId: string;
  cabinetName: string;
}

const MAX_CHIPS = 3;

type EtatBrique = 'fixe' | 'ajoutee' | 'retiree';

/**
 * Diff visuel des rôles d'un membre : sans override, tout est « fixe » (hérité
 * du compte) ; avec override, chaque brique est fixe (dans les deux), ajoutée
 * (override seulement) ou retirée (compte seulement — barrée).
 */
const diffBriques = (
  membre: CollaborateurBriquesPlateformeDto
): { brique: string; etat: EtatBrique }[] => {
  if (membre.briquesPlateforme == null) {
    return membre.briquesCompte.map((brique) => ({ brique, etat: 'fixe' as const }));
  }

  const compte = new Set(membre.briquesCompte);
  const override = new Set(membre.briquesPlateforme);
  const union = [
    ...membre.briquesCompte,
    ...membre.briquesPlateforme.filter((b) => !compte.has(b))
  ];
  return union.map((brique) => ({
    brique,
    etat: override.has(brique) ? (compte.has(brique) ? 'fixe' : 'ajoutee') : 'retiree'
  }));
};

const BriquesChips: React.FC<{ membre: CollaborateurBriquesPlateformeDto }> = ({ membre }) => {
  const chips = diffBriques(membre).sort(
    (a, b) => Number(a.etat === 'fixe') - Number(b.etat === 'fixe')
  );
  const visibles = chips.slice(0, MAX_CHIPS);
  const restantes = chips.slice(MAX_CHIPS);

  if (chips.length === 0) {
    return <span className="text-xs italic text-amber-600">aucune brique</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibles.map(({ brique, etat }) => (
        <span
          key={brique}
          title={
            etat === 'ajoutee'
              ? 'Ajouté dans cette plateforme'
              : etat === 'retiree'
                ? 'Retiré dans cette plateforme'
                : 'Hérité du compte'
          }
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
            etat === 'ajoutee'
              ? 'border border-primary/30 bg-primary/5 text-primary'
              : etat === 'retiree'
                ? 'border border-gray-200 bg-white text-gray-400 line-through decoration-gray-300'
                : 'bg-gray-100 text-gray-700'
          }`}
        >
          {etat === 'ajoutee' ? `+ ${brique}` : brique}
        </span>
      ))}
      {restantes.length > 0 && (
        <span className="text-xs text-gray-400" title={restantes.map((c) => c.brique).join(', ')}>
          + {restantes.length} autre{restantes.length > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
};

/**
 * Panel à deux vues d'un cabinet de la plateforme : la liste de SES collaborateurs
 * (chargée au clic sur la PM — jamais toute la plateforme d'un coup), puis
 * l'édition des briques d'un membre (BriquesCollaborateurForm embarqué, retour à la
 * liste au succès). L'attribution reste keyée par compte (abpUserId).
 */
export const CabinetCollaborateursPanel: React.FC<CabinetCollaborateursPanelProps> = ({
  plateformeId,
  personneMoraleId,
  cabinetName
}) => {
  const { setPanelActions } = usePanel();

  const [collaborateurs, setCollaborateurs] = useState<CollaborateurBriquesPlateformeDto[] | null>(null);
  const [selection, setSelection] = useState<CollaborateurBriquesPlateformeDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const res = plateformeId
        ? await attributionsPlateformesAPI.getCollaborateursCabinet({
            plateformeId: plateformeId,
            personneMoraleId
          })
        : await attributionsPlateformesAPI.getCollaborateursCabinetGouvernance({ personneMoraleId });
      setCollaborateurs(res.items);
      setError(null);
    } catch (err) {
      console.error('Erreur de chargement des collaborateurs du cabinet:', err);
      setError('Impossible de charger les collaborateurs de ce cabinet.');
    }
  }, [plateformeId, personneMoraleId]);

  useEffect(() => {
    charger();
  }, [charger]);

  // En vue liste, le panel n'a pas d'action de soumission : le pied de panel
  // est repris par BriquesCollaborateurForm quand un membre est sélectionné.
  useEffect(() => {
    if (!selection) {
      setPanelActions(undefined);
    }
  }, [selection, setPanelActions]);

  if (selection) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelection(null)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          {cabinetName}
        </button>
        <BriquesCollaborateurForm
          plateformeId={plateformeId}
          abpUserId={selection.abpUserId}
          collaborateurName={selection.nom}
          stayOpenOnSuccess
          onSuccess={() => {
            setSelection(null);
            charger();
          }}
        />
      </div>
    );
  }

  if (!collaborateurs) {
    return error ? <InlineError message={error} /> : <p className="text-sm text-gray-500">Chargement…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {collaborateurs.length} collaborateur{collaborateurs.length > 1 ? 's' : ''} actif{collaborateurs.length > 1 ? 's' : ''} —
        cliquer sur un collaborateur pour ajuster ses briques dans cette plateforme.
      </p>

      <div className="divide-y overflow-hidden rounded-xl border">
        {collaborateurs.map((membre) => (
          <button
            key={membre.abpUserId}
            type="button"
            onClick={() => setSelection(membre)}
            className="flex w-full items-center gap-4 bg-white px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div className="min-w-0 w-56 shrink-0">
              <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <span className="truncate">{membre.nom}</span>
                {membre.isOwner && (
                  <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">
                    Gérant
                  </span>
                )}
              </p>
              {membre.email && <p className="mt-0.5 truncate text-xs text-gray-500">{membre.email}</p>}
            </div>
            <div className="min-w-0 flex-1">
              <BriquesChips membre={membre} />
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
          </button>
        ))}
        {collaborateurs.length === 0 && (
          <p className="bg-white px-4 py-6 text-center text-sm text-gray-500">
            Aucun collaborateur actif dans ce cabinet.
          </p>
        )}
      </div>

      <InlineError message={error} />
    </div>
  );
};
